import { useEffect, useMemo, useState } from "react";
import {
  Check,
  CheckCircle2,
  Clock3,
  RefreshCw,
  ShieldCheck,
  X,
} from "lucide-react";
import { api, ApiError, query } from "../api";
import type {
  ApiList,
  ApprovalItem,
  LeaveRequest,
  ShiftChange,
  Team,
  User,
} from "../types";
import {
  dateLabel,
  dateRangeLabel,
  LEAVE_LABELS,
  SHIFT_LABELS,
} from "../utils";
import {
  Alert,
  Button,
  Empty,
  Loading,
  Modal,
  PageHeader,
  SelectField,
  StatusBadge,
  TextareaField,
} from "../components/ui";

type RawApproval =
  | ApprovalItem
  | (LeaveRequest & { kind?: string })
  | (ShiftChange & { kind?: string });

const approvalDetailLabels: Record<string, string> = {
  annual: "Yıllık izin",
  excuse: "Mazeret izni",
  marriage: "Evlilik izni",
  bereavement: "Cenaze izni",
  medical: "Rapor",
  training: "Eğitim",
  assignment: "Görev",
  flight: "Uçuş",
  military: "Askerlik",
  other: "Diğer",
  SABAH: "Sabah",
  OGLE: "Öğle",
  GECE: "Gece",
  OFF: "OFF",
};

function approvalDate(value: string): string {
  const dates = value.match(/\d{4}-\d{2}-\d{2}/g);
  if (!dates?.length) return value;
  return dates.length > 1
    ? dateRangeLabel(dates[0]!, dates[1]!)
    : dateLabel(dates[0]!);
}

function approvalDetails(value?: string): string | undefined {
  if (!value) return value;
  return value.replace(
    /\b(annual|excuse|marriage|bereavement|medical|training|assignment|flight|military|other|SABAH|OGLE|GECE|OFF)\b/g,
    (token) => approvalDetailLabels[token] ?? token,
  );
}

function normalize(item: RawApproval): ApprovalItem {
  if ("dateLabel" in item && "title" in item && "kind" in item)
    return {
      ...item,
      dateLabel: approvalDate(item.dateLabel),
      details: approvalDetails(item.details),
    };
  if ("type" in item)
    return {
      kind:
        item.status === "CANCELLATION_PENDING"
          ? "cancellation"
          : item.status === "CHANGE_PENDING"
            ? "change"
            : "leave",
      id: item.id,
      displayName: item.displayName,
      team: item.team,
      dateLabel: dateRangeLabel(item.startDate, item.endDate),
      title: LEAVE_LABELS[item.type],
      description: item.description,
      status: item.status,
      details: `${item.units} gün`,
    };
  return {
    kind: "shift-change",
    id: item.id,
    displayName: item.displayName,
    team: item.team,
    dateLabel: item.date,
    title: `${SHIFT_LABELS[item.requestedShift]} gelecek`,
    description: item.description,
    status: item.status,
    details: `${SHIFT_LABELS[item.currentShift]} → ${SHIFT_LABELS[item.requestedShift]}`,
  };
}

function approvalList(
  value: ApiList<RawApproval> | RawApproval[] | { approvals: RawApproval[] },
): ApprovalItem[] {
  const items = Array.isArray(value)
    ? value
    : "items" in value
      ? value.items
      : value.approvals;
  return items.map(normalize);
}

export function ApprovalsScreen({
  currentUser,
  notify,
}: {
  currentUser: User;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [team, setTeam] = useState<Team | "">(
    currentUser.role === "chief"
      ? (currentUser.mainTeam ?? currentUser.assignment?.mainTeam ?? "")
      : "",
  );
  const [kind, setKind] = useState("");
  const [acting, setActing] = useState<{
    item: ApprovalItem;
    action: "approve" | "reject";
  } | null>(null);
  const [reason, setReason] = useState("");
  const [decisionWarnings, setDecisionWarnings] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const focusedId = new URLSearchParams(window.location.hash.split("?", 2)[1] ?? "").get("focus");
  async function load() {
    setLoading(true);
    setError("");
    try {
      setItems(
        approvalList(
          await api<
            ApiList<RawApproval> | RawApproval[] | { approvals: RawApproval[] }
          >(`/api/approvals${query({ team, kind })}`),
        ),
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Onay listesi yüklenemedi.",
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, [team, kind]);
  useEffect(() => {
    if (loading || !focusedId) return;
    window.setTimeout(() => document.getElementById(`approval-${focusedId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
  }, [focusedId, loading, items]);
  async function submit() {
    if (!acting) return;
    setSubmitting(true);
    try {
      if (acting.item.kind === "mission") {
        await api(`/api/missions/${acting.item.id}/decision`, {
          method: "POST",
          body: {
            action: acting.action,
            version: acting.item.version,
            reason: reason.trim() || null,
            overrideConfirmed: decisionWarnings.length > 0,
            overrideReason: decisionWarnings.length > 0 ? reason.trim() || null : null,
          },
        });
      } else {
        await api(
          `/api/approvals/${acting.item.kind}/${acting.item.id}/${acting.action}`,
          { method: "POST", body: { reason: reason.trim() || null } },
        );
      }
      notify(
        acting.action === "approve" ? "Kayıt onaylandı." : "Kayıt reddedildi.",
      );
      setActing(null);
      setReason("");
      setDecisionWarnings([]);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409 && typeof caught.details === "object" && caught.details !== null) {
        const warnings = (caught.details as { warnings?: Array<{ message?: string }> }).warnings;
        if (warnings?.length) {
          setDecisionWarnings(warnings.map((warning) => warning.message ?? "Güncel personel uyarısı"));
          notify("Personel durumu değişti; uyarıları inceleyip gerekçeyle tekrar onaylayın.", "info");
          return;
        }
      }
      notify(
        caught instanceof ApiError ? caught.message : "İşlem tamamlanamadı.",
        "danger",
      );
    } finally {
      setSubmitting(false);
    }
  }
  const groups = useMemo(
    () =>
      items.reduce<Record<string, ApprovalItem[]>>((result, item) => {
        (result[item.dateLabel] ??= []).push(item);
        return result;
      }, {}),
    [items],
  );
  return (
    <div className="page">
      <PageHeader
        eyebrow="YETKİLİ İŞLEM"
        title="Onaylar"
        description="Başteknisyen veya aktif vekilden ilk işlem yapanın kararı geçerlidir."
        actions={
          <Button variant="ghost" onClick={() => void load()}>
            <RefreshCw size={18} />
            Yenile
          </Button>
        }
      />
      <Alert>
        <ShieldCheck size={18} /> Yalnızca yetkili olduğunuz ekip ve aktif
        vekâlet tarihindeki talepler gösterilir.
      </Alert>
      <div className="inline-filters">
        {currentUser.role === "admin" ? (
          <SelectField
            label="Ekip"
            value={team}
            onChange={(event) => setTeam(event.target.value as Team | "")}
          >
            <option value="">Tüm ekipler</option>
            {["A", "B", "C", "D"].map((item) => (
              <option key={item}>{item}</option>
            ))}
          </SelectField>
        ) : null}
        <SelectField
          label="İşlem türü"
          value={kind}
          onChange={(event) => setKind(event.target.value)}
        >
          <option value="">Tüm talepler</option>
          <option value="leave">İzin</option>
          <option value="shift-change">Vardiya değişikliği</option>
          <option value="cancellation">İptal</option>
          <option value="change">Değişiklik</option>
          <option value="mission">Görev</option>
        </SelectField>
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {loading ? (
        <Loading label="Bekleyen onaylar yükleniyor" />
      ) : items.length ? (
        <div className="approval-groups">
          {Object.entries(groups).map(([label, entries]) => (
            <section key={label}>
              <h2>
                <Clock3 size={18} />
                {label}
              </h2>
              {entries.map((item) => (
                <article
                  id={`approval-${item.id}`}
                  className={`approval-card ${String(item.id) === focusedId ? "record-card--focused" : ""}`}
                  key={`${item.kind}-${item.id}`}
                >
                  <div className="approval-card__identity">
                    <span
                      className={`team-dot team-dot--${(item.team ?? "management").toLowerCase()}`}
                    >
                      {item.kind === "mission" ? "Görev" : item.team ?? "R1/R2"}
                    </span>
                    <div>
                      <strong>{item.displayName}</strong>
                      <span>
                        {item.title} · {item.details}
                      </span>
                    </div>
                  </div>
                  <StatusBadge status={item.status} />
                  {item.description ? (
                    <p className="private-description">
                      <span>{item.kind === "mission" ? "Genel açıklama" : "Özel açıklama"}</span>
                      {item.description}
                    </p>
                  ) : null}
                  <footer>
                    <Button
                      variant="danger"
                      onClick={() => { setDecisionWarnings([]); setReason(""); setActing({ item, action: "reject" }); }}
                    >
                      <X size={17} />
                      Reddet
                    </Button>
                    <Button
                      onClick={() => { setDecisionWarnings([]); setReason(""); setActing({ item, action: "approve" }); }}
                    >
                      <Check size={17} />
                      Onayla
                    </Button>
                  </footer>
                </article>
              ))}
            </section>
          ))}
        </div>
      ) : (
        <Empty
          icon={<CheckCircle2 />}
          title="Bekleyen işlem yok"
          text="Yetkiniz dahilinde sonuçlandırılmayı bekleyen talep bulunmuyor."
        />
      )}
      <Modal
        open={Boolean(acting)}
        onClose={() => { setActing(null); setDecisionWarnings([]); setReason(""); }}
        title={acting?.action === "approve" ? "Talebi onayla" : "Talebi reddet"}
      >
        {acting ? (
          <div className="form-stack">
            <p>
              <strong>{acting.item.displayName}</strong> için{" "}
              <strong>{acting.item.title}</strong> kaydını{" "}
              {acting.action === "approve" ? "onaylamak" : "reddetmek"}{" "}
              üzeresiniz.
            </p>
            <TextareaField
              label={
                acting.action === "reject"
                  ? "Ret gerekçesi"
                  : decisionWarnings.length
                    ? "Uyarıyı aşma gerekçesi"
                    : "İşlem notu (isteğe bağlı)"
              }
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required={acting.action === "reject" || decisionWarnings.length > 0}
              maxLength={500}
            />
            {decisionWarnings.length ? (
              <Alert tone="danger">
                <div>
                  <strong>Kaydetme anındaki güncel uyarılar</strong>
                  <ul>{decisionWarnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
                </div>
              </Alert>
            ) : null}
            <div className="modal-actions">
              <Button variant="ghost" onClick={() => setActing(null)}>
                Vazgeç
              </Button>
              <Button
                variant={acting.action === "reject" ? "danger" : "primary"}
                onClick={() => void submit()}
                loading={submitting}
                disabled={(acting.action === "reject" || decisionWarnings.length > 0) && reason.trim().length < 3}
              >
                {acting.action === "approve" ? "Onayla" : "Reddet"}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
